import { unwrapList, unwrapObject, getAccessToken } from './apiUtils';
import { call, toNumber } from './SalesService';
import { listClients } from './ClientService';
import { searchBooks } from './BooksService';
import {
    PrintCompany,
    PrintLine,
    PrintTicket,
    PrintInvoice,
    renderTicketHtml,
    renderInvoiceHtml,
    renderInvoiceEmailHtml
} from '../utils/documentTemplates';
import { printHtml } from '../utils/printHtml';
import { readPaperWidth } from '../utils/printerConfig';

/**
 * Documentos imprimibles: el ticket y la factura. Se leen del servidor en el
 * momento de imprimir (no de lo que haya en pantalla), asi una reimpresion sale
 * igual que el original aunque hayan pasado dias.
 */
const ROUTES = {
    ticket: '/tickets_get',
    invoice: '/invoices_get',
    companies: '/companies-list',
    sendEmail: '/invoices_send_email',
    sendMail: '/send_mail'
};

const toLine = (raw: any): PrintLine => ({
    description: raw.description || raw.book_title || 'Linea sin descripcion',
    quantity: toNumber(raw.quantity, 1),
    unitPrice: toNumber(raw.unit_price),
    discountPct: toNumber(raw.discount_pct),
    taxRate: toNumber(raw.tax_rate),
    lineTotal: toNumber(raw.line_total)
});

/** Para comparar titulos sin que un espacio de mas o las mayusculas estorben. */
const sameTitle = (a: string, b: string): boolean =>
    String(a || '').trim().replace(/\s+/g, ' ').toLowerCase() ===
    String(b || '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Completa cada linea con el ISBN y la editorial de su libro, que es lo que la
 * factura imprime bajo el titulo.
 *
 * La linea de factura NO guarda el libro, solo su titulo, asi que hay que
 * buscarlo en el catalogo. Una busqueda por titulo distinto (no por linea) y
 * todas en paralelo. Se busca en TODO el catalogo, sin filtrar por almacen:
 * aqui no interesa el stock, y con el filtro un libro de otra libreria no
 * saldria.
 *
 * Solo se acepta la coincidencia si el titulo es exacto y unico. Con dos libros
 * del mismo titulo no hay forma de saber cual se vendio, y es preferible una
 * linea sin ISBN que una con el ISBN de otro libro. Si el catalogo no responde,
 * la factura sale igual, sin esos datos: no es motivo para no poder imprimir.
 */
const addBookRefs = async (lines: PrintLine[]): Promise<void> => {
    const titles = lines
        .map(l => l.description)
        .filter((title, i, all) => title && all.indexOf(title) === i);

    await Promise.all(titles.map(async title => {
        try {
            const exact = (await searchBooks(title, 50, '')).filter(b => sameTitle(b.title, title));
            if (exact.length !== 1) return;
            lines.forEach(l => {
                if (l.description === title) {
                    l.isbn = exact[0].isbn;
                    l.publisher = exact[0].publisher;
                }
            });
        } catch {
            // Sin catalogo la factura sale sin ISBN, pero sale.
        }
    }));
};

const toBool = (raw: any): boolean => raw === true || raw === 't' || raw === 'true';

/**
 * Datos de la sociedad emisora que la tabla `companies` todavia no guarda, y
 * que la factura NO puede imprimir en blanco: el pie registral es obligatorio
 * en los documentos de una S.L., y sin la cuenta no hay forma de pagar por
 * transferencia.
 *
 * Es un respaldo, no un valor cableado: en getCompany el dato del servidor
 * manda siempre, asi que el dia que `companies` tenga estas columnas esto deja
 * de usarse solo, sin tocar nada.
 *
 * Los valores salen de una factura emitida y estan verificados. Si cambian el
 * domicilio, la cuenta o los datos registrales, hay que tocarlos aqui hasta
 * que vengan de `companies`.
 */
const COMPANY_FALLBACK = {
    branch: '-DCHO-ECO',
    fax: '913194373',
    associateNumber: '718-43',
    registryNote: 'Sociedad Limitada inscrita en el Reg. Merc. de Madrid, tomo 16.454, Libro 0, Folio 153, Sección 8, Hoja M-279967, Inscripción 1.'
};

/** Cuenta que sale en el bloque TRANSFERENCIA. Mismo caso que COMPANY_FALLBACK. */
const INVOICE_BANK = {
    name: 'BANCO SABADELL',
    iban: 'ES43 0081 0532 4600 0111 8216',
    swift: 'BSABESBB'
};

const EMPTY_COMPANY: PrintCompany = { name: '', cif: '', address: '', phone: '', email: '', ...COMPANY_FALLBACK };

/** Datos fiscales de la sociedad que emite. Si no se pueden leer, el documento sale con lo que traiga. */
const getCompany = async (companyId: any, fallbackName: string, fallbackCif: string): Promise<PrintCompany> => {
    const fallback = { ...EMPTY_COMPANY, name: fallbackName || '', cif: fallbackCif || '' };
    try {
        const accessToken = getAccessToken();
        if (!accessToken || companyId === undefined || companyId === null) return fallback;

        const res = await call(ROUTES.companies, { access_token: accessToken, page: 1, page_size: 200 });
        const raw = unwrapList(res).find(c => String(c.id) === String(companyId));
        if (!raw) return fallback;
        return {
            name: raw.name || fallback.name,
            cif: raw.cif || fallback.cif,
            address: raw.address || '',
            phone: raw.phone || '',
            email: raw.email || '',
            // Cuando companies traiga estas columnas, ganan sobre el respaldo.
            branch: raw.branch || COMPANY_FALLBACK.branch,
            fax: raw.fax || COMPANY_FALLBACK.fax,
            associateNumber: raw.associate_number || COMPANY_FALLBACK.associateNumber,
            registryNote: raw.registry_note || COMPANY_FALLBACK.registryNote
        };
    } catch {
        return fallback;
    }
};

const fetchRaw = async (route: string, id: string): Promise<any> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const raw = unwrapObject(await call(route, { access_token: accessToken, id }));
    if (!raw || !raw.id) throw new Error('No se encontro el documento.');
    return raw;
};

export const loadTicket = async (ticketId: string): Promise<PrintTicket> => {
    const raw = await fetchRaw(ROUTES.ticket, ticketId);
    const original = raw.original_series ? `${raw.original_series}-${raw.original_ticket_number}` : '';

    return {
        number: `${raw.series}-${raw.ticket_number}`,
        type: raw.ticket_type === 'return' ? 'return' : 'sale',
        createdAt: new Date(raw.created_at || Date.now()),
        paymentMethod: raw.payment_method || '',
        subtotal: toNumber(raw.subtotal),
        taxAmount: toNumber(raw.tax_amount),
        total: toNumber(raw.total),
        isTaxFree: toBool(raw.is_tax_free),
        clientName: raw.client_name || '',
        clientCif: raw.client_cif || '',
        invoiceNumber: raw.invoice_number || '',
        originalNumber: original,
        lines: (Array.isArray(raw.lines) ? raw.lines : []).map(toLine),
        company: await getCompany(raw.company_id, '', '')
    };
};

/**
 * La direccion y el telefono del cliente no vienen en la factura (invoices_get
 * solo trae nombre, CIF y email): se buscan en su ficha, si se puede. Con dos
 * clientes del mismo nombre no hay forma de distinguir y se deja en blanco.
 */
const getClientCard = async (clientId: any, clientName: string): Promise<{ address: string; phone: string }> => {
    const empty = { address: '', phone: '' };
    try {
        if (clientId === undefined || clientId === null || !clientName) return empty;
        const found = await listClients(clientName);
        const client = found.find(c => String(c.id) === String(clientId));
        if (!client) return empty;
        return { address: client.address || '', phone: client.phone || '' };
    } catch {
        return empty;
    }
};

export const loadInvoice = async (invoiceId: string): Promise<PrintInvoice> => {
    const raw = await fetchRaw(ROUTES.invoice, invoiceId);
    const lines = (Array.isArray(raw.lines) ? raw.lines : []).map(toLine);

    const [card] = await Promise.all([
        getClientCard(raw.client_id, raw.client_name || ''),
        addBookRefs(lines)
    ]);

    return {
        number: raw.invoice_number || String(raw.id),
        issueDate: new Date(raw.issue_date || raw.created_at || Date.now()),
        status: raw.status || '',
        paymentMethod: raw.payment_instrument_ref || '',
        subtotal: toNumber(raw.subtotal),
        taxAmount: toNumber(raw.tax_amount),
        total: toNumber(raw.total),
        isTaxFree: toBool(raw.is_tax_free),
        clientName: raw.client_name || '',
        clientCif: raw.client_cif || '',
        clientEmail: raw.client_email || '',
        clientAddress: card.address,
        ticketNumber: '',
        lines,
        company: await getCompany(raw.company_id, raw.company_name || '', raw.company_cif || ''),

        // Campos del formato de factura que hoy si tienen de donde salir. Los
        // demas (delegacion y nº de asociado del emisor, poblacion y pais del
        // cliente, gastos de envio y gestion, envio, aduana, banco y pie
        // registral) no existen en la base de datos: la plantilla los imprime
        // con la etiqueta vacia hasta que los haya.
        clientPhone: card.phone,
        /** REF. CLIENTE: el numero de cliente es su id. */
        clientRef: raw.client_id === undefined || raw.client_id === null ? '' : String(raw.client_id),
        currency: raw.currency || 'EUR',
        bank: INVOICE_BANK
    };
};

/** Manda el ticket a imprimir. `ticketId` es tickets.id. */
export const printTicket = async (ticketId: string): Promise<void> => {
    // Rollo termico: el ancho (58 u 80 mm) es el que se haya fijado en Ajustes.
    const width = readPaperWidth();
    await printHtml(renderTicketHtml(await loadTicket(ticketId), width), { rollWidthMm: width });
};

/**
 * Manda la factura a imprimir. `ticketNumber` es opcional: si se conoce, sale
 * en la cabecera para poder cruzar la factura con su ticket.
 */
export const printInvoice = async (invoiceId: string, ticketNumber = ''): Promise<void> => {
    const invoice = await loadInvoice(invoiceId);
    await printHtml(renderInvoiceHtml({ ...invoice, ticketNumber }));
};

/**
 * Ticket de prueba para comprobar la impresora desde Ajustes: sale con el mismo
 * formato y ancho de rollo que un ticket real. No se registra en ningun sitio.
 */
export const printTestTicket = async (): Promise<void> => {
    const width = readPaperWidth();
    const ticket: PrintTicket = {
        number: 'PRUEBA',
        type: 'sale',
        createdAt: new Date(),
        paymentMethod: 'cash',
        subtotal: 9.62,
        taxAmount: 0.38,
        total: 10,
        isTaxFree: false,
        clientName: '',
        clientCif: '',
        invoiceNumber: '',
        originalNumber: '',
        lines: [{ description: 'Prueba de impresión: ñ á é í ó ú € ¿?', quantity: 1, unitPrice: 10, discountPct: 0, taxRate: 4, lineTotal: 10 }],
        company: { name: 'PRUEBA DE IMPRESIÓN', cif: '', address: `Rollo de ${width} mm`, phone: '', email: '' }
    };
    await printHtml(renderTicketHtml(ticket, width), { rollWidthMm: width });
};

/**
 * Manda al cliente la factura por email, con /send_mail (el envio real), y
 * despues deja constancia en la factura con /invoices_send_email.
 *
 * Sin `email`, la direccion sale de la ficha del cliente (la factura la trae en
 * clientEmail). Si el envio falla, lanza: no se deja constancia de algo que no
 * salio. Si lo que falla es la constancia, NO: el correo ya esta enviado y
 * avisar de un fallo al cajero le haria reenviarlo.
 */
export const sendInvoiceEmail = async (invoiceId: string, email?: string, ticketNumber = ''): Promise<void> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const invoice = await loadInvoice(invoiceId);
    const to = (email || invoice.clientEmail || '').trim();
    if (!to) throw new Error('El cliente no tiene email.');

    await call(ROUTES.sendMail, {
        access_token: accessToken,
        to,
        subject: `Factura ${invoice.number} - ${invoice.company.name || 'Marcial Pons'}`,
        content: renderInvoiceEmailHtml({ ...invoice, ticketNumber })
    });

    try {
        await call(ROUTES.sendEmail, { access_token: accessToken, id: invoiceId, email: to });
    } catch {
        // Solo era la constancia.
    }
};

/** A quien va una factura: el nombre del cliente y el email que tiene registrado ('' si no tiene). */
export const invoiceRecipient = async (invoiceId: string): Promise<{ clientName: string; email: string }> => {
    const invoice = await loadInvoice(invoiceId);
    return { clientName: invoice.clientName, email: invoice.clientEmail };
};

/** El mismo HTML que se manda a imprimir, para enseñarlo en pantalla (visor de documentos). */
export const invoiceHtml = async (invoiceId: string, ticketNumber = ''): Promise<string> => {
    const invoice = await loadInvoice(invoiceId);
    return renderInvoiceHtml({ ...invoice, ticketNumber });
};

